import type { TreatmentAcceptanceOverviewKpiData } from '@services/api/dashboard/treatment-acceptance-overview.api'
import type { DashboardMetricItemData } from '../types/dashboardMetrics.types'
import type { RangeKey } from '@dataTypes/dashboard.types'
import { getOverviewTreatmentAcceptanceTopMetrics } from '@components/pages/Dashboard/Overview/overviewMetrics.mockData'
import {
  getTreatmentAcceptanceOverviewChartConfig,
  getTreatmentAcceptanceOverviewExtras,
  type HalfDonutConfig,
  type TreatmentAcceptanceOverviewExtras,
} from '@components/pages/Dashboard/Overview/dashboardOverview.mockData'
import {
  isDashboardDevMocksEnabled,
  withDashboardMetricShellValues,
} from '@utils/dashboardDevMocks.util'

const EMPTY_TX_ACCEPTANCE_EXTRAS: TreatmentAcceptanceOverviewExtras = {
  txPresented: '—',
  txCompletedActual: '—',
  txCompletedTotal: '—',
}

const EMPTY_TX_ACCEPTANCE_CHART: HalfDonutConfig = {
  primaryValue: 0,
  secondaryValue: 0,
  slices: [
    { name: 'Insurance', value: 0, color: 'var(--blue-400)' },
    { name: 'Patient', value: 0, color: 'var(--orange-300)' },
  ],
  valueFormatter: (val: number): string => `${val}%`,
}

export function mapTreatmentAcceptanceOverviewToTopMetrics(
  kpis: TreatmentAcceptanceOverviewKpiData,
): DashboardMetricItemData[] {
  return [
    {
      id: 'tx-presented',
      leftLabel: 'Tx presented',
      value: kpis.txPresented,
    },
    {
      id: 'presented-value',
      leftLabel: 'Presented value',
      value: kpis.presentedValue,
    },
    {
      id: 'accepted-value',
      leftLabel: 'Accepted value',
      value: kpis.acceptedValue,
    },
  ]
}

export function mapTreatmentAcceptanceOverviewToExtras(
  kpis: TreatmentAcceptanceOverviewKpiData,
): TreatmentAcceptanceOverviewExtras {
  return {
    txPresented: kpis.txPresented,
    txCompletedActual: kpis.txCompletedActual,
    txCompletedTotal: kpis.txCompletedTotal,
  }
}

export function buildTreatmentAcceptanceOverviewChart(
  kpis: TreatmentAcceptanceOverviewKpiData | null | undefined,
  range: RangeKey,
): HalfDonutConfig {
  const mock = getTreatmentAcceptanceOverviewChartConfig(range)
  if (!kpis) {
    return isDashboardDevMocksEnabled() ? mock : EMPTY_TX_ACCEPTANCE_CHART
  }
  const insurance = kpis.primaryValue
  const patient = kpis.secondaryValue
  return {
    primaryValue: insurance,
    secondaryValue: patient,
    slices: [
      { name: 'Insurance', value: insurance, color: 'var(--blue-400)' },
      { name: 'Patient', value: patient, color: 'var(--orange-300)' },
    ],
    valueFormatter: (val) => `${val}%`,
  }
}

export function buildTreatmentAcceptanceOverviewSection(
  range: RangeKey,
  kpis: TreatmentAcceptanceOverviewKpiData | null | undefined,
): {
  topMetrics: DashboardMetricItemData[]
  extras: TreatmentAcceptanceOverviewExtras
  chart: HalfDonutConfig
} {
  if (!kpis) {
    const useMocks = isDashboardDevMocksEnabled()
    return {
      topMetrics: withDashboardMetricShellValues(
        getOverviewTreatmentAcceptanceTopMetrics(range),
        useMocks,
      ),
      extras: useMocks
        ? getTreatmentAcceptanceOverviewExtras(range)
        : EMPTY_TX_ACCEPTANCE_EXTRAS,
      chart: useMocks
        ? getTreatmentAcceptanceOverviewChartConfig(range)
        : EMPTY_TX_ACCEPTANCE_CHART,
    }
  }
  return {
    topMetrics: mapTreatmentAcceptanceOverviewToTopMetrics(kpis),
    extras: mapTreatmentAcceptanceOverviewToExtras(kpis),
    chart: buildTreatmentAcceptanceOverviewChart(kpis, range),
  }
}
